import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useWalletAccess } from '@/features/wallet-access/wallet-access-provider';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { walletSettingsPath } from '@/pages/settings/settings-tab-keys';
import { isNativeSuiAsset } from '@/shared/modules/wallet/is-native-sui-asset';
import { useCryptoAssets } from '@/shared/modules/wallet/use-crypto-assets';
import { UserSuiWalletStatus } from '@/shared/modules/wallet/use-privy-wallet';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { ReceiveModal } from '../transfers/receive/receive-modal';
import { useOpenReceive } from '../transfers/receive/use-open-receive';
import { SendModal } from '../transfers/send/send-modal';
import { useOpenSend } from '../transfers/send/use-open-send';
import { CryptoAssetsActions } from './crypto-assets-actions';
import { CryptoAssetsContent } from './crypto-assets-content';
import { CryptoPerformanceSelection } from './types';

enum TransferModal {
  Send = 'send',
  Receive = 'receive',
}

interface CryptoAssetsProps {
  activePositionId?: string;
  onSelectPosition: (_selection: CryptoPerformanceSelection | null) => void;
}

const CryptoAssets = ({ activePositionId, onSelectPosition }: CryptoAssetsProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const isPersonalUser = selectedUserRole === 'user';
  const wallet = useCryptoAssets({ enabled: isPersonalUser });
  const { assets, isLoading, walletStatus, suiAddress } = wallet;
  const transfers = useTransferEntryPoints(isMobile);
  const defaultReceiveAsset = assets.find(isNativeSuiAsset) ?? assets[0];

  useEffect(() => {
    if (isLoading || !activePositionId) {
      return;
    }

    if (!assets.some((asset) => asset.positionId === activePositionId)) {
      onSelectPosition(null);
    }
  }, [activePositionId, assets, isLoading, onSelectPosition]);

  if (!isPersonalUser) {
    return null;
  }

  return (
    <section>
      <CryptoAssetsActions
        isMobile={isMobile}
        canSend={walletStatus === UserSuiWalletStatus.READY && assets.length > 0}
        canReceive={walletStatus === UserSuiWalletStatus.READY}
        onSend={transfers.openSend}
        onReceive={() => transfers.openReceive(defaultReceiveAsset)}
      />
      <CryptoAssetsContent
        wallet={wallet}
        isMobile={isMobile}
        activePositionId={activePositionId}
        onSelectPosition={onSelectPosition}
        onSetUpWallet={() => navigate(walletSettingsPath)}
      />
      <SendModal
        open={transfers.modal === TransferModal.Send}
        onClose={transfers.close}
        assets={assets}
        senderAddress={suiAddress}
      />
      <ReceiveModal
        open={transfers.modal === TransferModal.Receive}
        onClose={transfers.close}
        suiAddress={suiAddress}
      />
    </section>
  );
};

function useTransferEntryPoints(isMobile: boolean) {
  const [modal, setModal] = useState<TransferModal | null>(null);
  const openSend = useOpenSend({ isMobile, onOpenModal: () => setModal(TransferModal.Send) });
  const openUnlockedSend = useOpenUnlockedSend(openSend);
  const openReceive = useOpenReceive({
    isMobile,
    onOpenModal: () => setModal(TransferModal.Receive),
  });
  return { modal, openSend: openUnlockedSend, openReceive, close: () => setModal(null) };
}

function useOpenUnlockedSend(openSend: () => void): () => void {
  const { recordActivity, unlock } = useWalletAccess();

  return () => {
    if (recordActivity()) {
      openSend();
      return;
    }

    void unlock().then((unlocked) => {
      if (unlocked) {
        openSend();
      }
    });
  };
}

export default CryptoAssets;
